/**
 * ViewingRequestDialog - Request Property Viewing Modal
 * Allows users to request property viewings with preferred dates/times
 */
import React, { useState } from 'react';
import { X, Calendar, Clock, CheckCircle, AlertCircle } from 'lucide-react';
import { cn } from '../../lib/utils';
import { getApiBaseUrl } from '../../lib/api';

const API_URL = getApiBaseUrl();

const TIME_PREFERENCES = [
  { value: 'morning', label: 'Morning (9am-12pm)' },
  { value: 'afternoon', label: 'Afternoon (12pm-5pm)' },
  { value: 'evening', label: 'Evening (5pm-8pm)' },
];

// Generate next 14 days for date selection
const generateDateOptions = () => {
  const dates = [];
  const today = new Date();
  
  for (let i = 1; i <= 14; i++) {
    const date = new Date(today);
    date.setDate(today.getDate() + i);
    dates.push({
      value: date.toISOString().split('T')[0],
      label: date.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' }),
      isWeekend: date.getDay() === 0 || date.getDay() === 6,
    });
  }
  
  return dates;
};

export const ViewingRequestDialog = ({
  isOpen,
  onClose,
  property,
  user,
}) => {
  const [selectedDates, setSelectedDates] = useState([]);
  const [selectedTimes, setSelectedTimes] = useState([]);
  const [message, setMessage] = useState('');
  const [phone, setPhone] = useState(user?.phone || '');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState(null);

  const dateOptions = generateDateOptions();

  const toggleDate = (dateValue) => {
    setSelectedDates(prev => 
      prev.includes(dateValue)
        ? prev.filter(d => d !== dateValue)
        : [...prev, dateValue].slice(0, 3) // Max 3 dates
    );
  };

  const toggleTime = (timeValue) => {
    setSelectedTimes(prev => 
      prev.includes(timeValue)
        ? prev.filter(t => t !== timeValue)
        : [...prev, timeValue]
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!user) {
      setError('Please log in to request a viewing');
      return;
    }

    if (selectedDates.length === 0) {
      setError('Please select at least one preferred date');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/search/viewing-requests`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          property_id: property.id,
          listing_id: property.listing_id,
          preferred_dates: selectedDates,
          preferred_times: selectedTimes,
          message: message || null,
          phone: phone || null,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to request viewing');
      }

      setIsSuccess(true);
      setTimeout(() => {
        onClose();
        setIsSuccess(false);
        setSelectedDates([]);
        setSelectedTimes([]);
        setMessage('');
      }, 2500);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const displayAddress = property?.address
    ? `${property.address.line1 || ''}, ${property.address.city || ''}`
    : 'this property';

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 z-50"
        onClick={onClose}
      />

      {/* Dialog */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div
          className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Request Viewing</h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {isSuccess ? (
            <div className="flex-1 flex flex-col items-center justify-center p-8 text-center">
              <CheckCircle className="w-16 h-16 text-emerald-500 mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Viewing Requested!</h3>
              <p className="text-gray-600">The landlord will confirm your viewing time soon.</p>
              <p className="text-sm text-gray-500 mt-2">You&apos;ll receive an email confirmation.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
              {/* Property Info */}
              <div className="p-4 bg-gray-50 border-b border-gray-200">
                <p className="text-sm text-gray-600">Viewing for:</p>
                <p className="font-medium text-gray-900">{displayAddress}</p>
                <p className="text-emerald-600 font-semibold">
                  £{property?.rent_amount?.toLocaleString()} {property?.rent_frequency || 'pcm'}
                </p>
              </div>

              <div className="p-4 space-y-5">
                {/* Login Required Warning */}
                {!user && (
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-amber-800">Login Required</p>
                      <p className="text-sm text-amber-700">Please log in to request a viewing.</p>
                    </div>
                  </div>
                )}

                {error && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                    {error}
                  </div>
                )}

                {/* Date Selection */}
                <div>
                  <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-3">
                    <Calendar className="w-4 h-4" />
                    Select preferred dates (up to 3)
                  </label>
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                    {dateOptions.map((date) => (
                      <button
                        key={date.value}
                        type="button"
                        onClick={() => toggleDate(date.value)}
                        disabled={!user}
                        className={cn(
                          'p-2 text-sm rounded-lg border transition-all text-center',
                          selectedDates.includes(date.value)
                            ? 'bg-emerald-600 text-white border-emerald-600'
                            : 'border-gray-300 text-gray-700 hover:border-emerald-500',
                          date.isWeekend && 'bg-gray-50',
                          !user && 'opacity-50 cursor-not-allowed'
                        )}
                      >
                        {date.label}
                      </button>
                    ))}
                  </div>
                  {selectedDates.length > 0 && (
                    <p className="mt-2 text-sm text-emerald-600">
                      {selectedDates.length} date{selectedDates.length !== 1 ? 's' : ''} selected
                    </p>
                  )}
                </div>

                {/* Time Preference */}
                <div>
                  <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-3">
                    <Clock className="w-4 h-4" />
                    Preferred times (optional)
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {TIME_PREFERENCES.map((time) => (
                      <button
                        key={time.value}
                        type="button"
                        onClick={() => toggleTime(time.value)}
                        disabled={!user}
                        className={cn(
                          'px-4 py-2 text-sm rounded-lg border transition-all',
                          selectedTimes.includes(time.value)
                            ? 'bg-emerald-600 text-white border-emerald-600'
                            : 'border-gray-300 text-gray-700 hover:border-emerald-500',
                          !user && 'opacity-50 cursor-not-allowed'
                        )}
                      >
                        {time.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Phone Number */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Contact Phone (for confirmation)
                  </label>
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    disabled={!user}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    placeholder="07xxx xxxxxx"
                  />
                </div>

                {/* Message */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Additional message (optional)
                  </label>
                  <textarea
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    disabled={!user}
                    rows={3}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 resize-none disabled:opacity-50 disabled:cursor-not-allowed"
                    placeholder="Any specific requirements or questions..."
                  />
                </div>
              </div>

              {/* Footer */}
              <div className="p-4 border-t border-gray-200 bg-gray-50">
                <button
                  type="submit"
                  disabled={isSubmitting || !user || selectedDates.length === 0}
                  className={cn(
                    'w-full py-3 px-4 rounded-lg font-medium flex items-center justify-center gap-2 transition-colors',
                    isSubmitting || !user || selectedDates.length === 0
                      ? 'bg-gray-400 text-white cursor-not-allowed'
                      : 'bg-emerald-600 text-white hover:bg-emerald-700'
                  )}
                >
                  {isSubmitting ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Requesting...
                    </>
                  ) : (
                    <>
                      <Calendar className="w-5 h-5" />
                      Request Viewing
                    </>
                  )}
                </button>
                <p className="mt-3 text-xs text-gray-500 text-center">
                  The landlord will confirm your viewing within 24 hours
                </p>
              </div>
            </form>
          )}
        </div>
      </div>
    </>
  );
};

export default ViewingRequestDialog;
